import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/core/AuthContext";
import { useFactory } from "@/core/hooks/useFactory";
import { fetchClaimById, updateClaimStatus, fetchClaimForRejection, regenerateClaimMagicToken } from "../api/claims";
import { fetchStepsByClaim, updateStepByNumber, rejectStepByNumber } from "../api/steps";
import { fetchCommentsByClaim, createFactoryCommentForClaim } from "../api/comments";
import { fetchTeamMembersByClaim } from "../api/team-members";
import { fetchProfileBasicInfo } from "@/core/api/profiles";
import { useToast } from "@/shared/hooks/use-toast";
import { queryKeys } from "@/core/lib/queryKeys";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { RichTextEditor } from "@/shared/ui/rich-text-editor";
import { ActionManager } from "./ActionManager";
import { RootCauseAnalysis } from "./RootCauseAnalysis";
import { PreventRecurrence } from "./PreventRecurrence";
import { TeamRecognition } from "./TeamRecognition";
import { FileAttachments } from "./FileAttachments";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { ArrowLeft, Send, Check, Building2, Calendar, User, Users, XCircle, AlertTriangle, Link, Copy } from "lucide-react";
import { Link as RouterLink } from "react-router-dom";
import { differenceInCalendarDays, format } from "date-fns";

const statusColors: Record<string, string> = {
  open: "bg-warning/20 text-warning border-warning/30",
  in_progress: "bg-chart-2/20 text-chart-2 border-chart-2/30",
  pending_supplier: "bg-chart-3/20 text-chart-3 border-chart-3/30",
  pending_approval: "bg-chart-4/20 text-chart-4 border-chart-4/30",
  closed: "bg-success/20 text-success border-success/30",
};

const stepStatusColors: Record<string, string> = {
  pending: "bg-muted/20 text-muted-foreground border-border",
  in_progress: "bg-chart-2/20 text-chart-2 border-chart-2/30",
  completed: "bg-success/20 text-success border-success/30",
  rejected: "bg-destructive/20 text-destructive border-destructive/30",
};

interface ClaimDetailsProps {
  claimId: string;
  onBack: () => void;
}

export function ClaimDetails({ claimId, onBack }: ClaimDetailsProps) {
  const { user } = useAuth();
  const { selectedFactory } = useFactory();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newComment, setNewComment] = useState("");
  const [editingStep, setEditingStep] = useState<number | null>(null);
  const [stepContent, setStepContent] = useState("");
  const [rejectingStep, setRejectingStep] = useState<{ stepNumber: number; title: string } | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");

  const { data: claim, isLoading } = useQuery({
    queryKey: ["mod_8d_claim", claimId],
    queryFn: () => fetchClaimById(claimId),
  });

  const { data: steps } = useQuery({
    queryKey: ["mod_8d_steps", claimId],
    queryFn: () => fetchStepsByClaim(claimId),
  });

  const { data: comments } = useQuery({
    queryKey: ["mod_8d_comments", claimId],
    queryFn: () => fetchCommentsByClaim(claimId),
  });

  const { data: teamMembers } = useQuery({
    queryKey: ["mod_8d_team_members", claimId],
    queryFn: () => fetchTeamMembersByClaim(claimId),
  });

  const updateStatusMutation = useMutation({
    mutationFn: (status: string) => updateClaimStatus(claimId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mod8dClaimDetails(claimId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.mod8dClaims(selectedFactory?.id ?? null) });
      toast({ title: "Status updated" });
    },
  });

  const regenerateTokenMutation = useMutation({
    mutationFn: () => regenerateClaimMagicToken(claimId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mod_8d_claim", claimId] });
      toast({ title: "Supplier link regenerated" });
    },
    onError: () => {
      toast({ title: "Failed to regenerate link", variant: "destructive" });
    },
  });

  const updateStepMutation = useMutation({
    mutationFn: async ({ stepNumber, content, status }: { stepNumber: number; content: string; status: string }) => {
      await updateStepByNumber(claimId, stepNumber, {
        content,
        status,
        completed_by: status === "completed" ? user?.id : null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mod8dSteps(claimId) });
      setEditingStep(null);
      toast({ title: "Step updated" });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("User not authenticated");
      await createFactoryCommentForClaim(claimId, user.id, newComment);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mod8dComments(claimId) });
      setNewComment("");
      toast({ title: "Comment added" });
    },
  });

  const rejectStepMutation = useMutation({
    mutationFn: async ({ stepNumber, rejectionNote, stepTitle }: { stepNumber: number; rejectionNote: string; stepTitle: string }) => {
      if (!user?.id) throw new Error("User not authenticated");
      
      // Get user profile for rejected_by name
      const userProfile = await fetchProfileBasicInfo(user.id);

      // Update step status to rejected using API
      await rejectStepByNumber(claimId, stepNumber, user.id, rejectionNote);

      // Get claim data for email notification
      const claimData = await fetchClaimForRejection(claimId);

      // Send rejection email to supplier
      if (claimData?.supplier?.contact_email) {
        await supabase.functions.invoke("send-step-rejection", {
          body: {
            claimId,
            claimNumber: claimData.claim_number,
            claimTitle: claimData.title,
            stepNumber,
            stepTitle,
            rejectionNote,
            rejectedByName: userProfile?.full_name || userProfile?.email || "Factory User",
            supplierEmail: claimData.supplier.contact_email,
            supplierName: claimData.supplier.name, // mod_8d.suppliers doesn't have contact_name
            factoryName: "Factory",
            magicToken: claimData.magic_token,
          },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.mod8dSteps(claimId) });
      setRejectingStep(null);
      setRejectionNote("");
      toast({ title: "Step rejected", description: "Supplier has been notified via email" });
    },
    onError: () => {
      toast({ title: "Failed to reject step", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="text-center py-8">Loading claim details...</div>;
  }

  if (!claim) {
    return <div className="text-center py-8">Claim not found</div>;
  }

  const expiresAt = claim.magic_token_expires_at ? new Date(claim.magic_token_expires_at) : null;
  const daysRemaining = expiresAt ? differenceInCalendarDays(expiresAt, new Date()) : null;
  const isExpired = daysRemaining !== null && daysRemaining < 0;
  const isNearExpiry = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 3;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-muted-foreground">
              {claim.claim_number}
            </span>
            <Badge variant="outline" className={statusColors[claim.status] || ""}>
              {claim.status.replace("_", " ")}
            </Badge>
          </div>
          <h1 className="text-2xl font-bold">{claim.title}</h1>
        </div>
        <Select value={claim.status} onValueChange={(v) => updateStatusMutation.mutate(v)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="pending_supplier">Pending Supplier</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* 8D Steps */}
          <Card>
            <CardHeader>
              <CardTitle>8D Report Steps</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="space-y-2">
                {steps?.map((step) => (
                  <AccordionItem key={step.step_number} value={`step-${step.step_number}`} className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className={stepStatusColors[step.status] || ""}>
                          {step.status === "completed" ? (
                            <Check className="h-3 w-3 mr-1" />
                          ) : null}
                          D{step.step_number}
                        </Badge>
                        <span className="font-medium">{step.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 pb-6">
                      {/* Special display for D1 - Team Formation */}
                      {step.step_number === 1 && teamMembers && teamMembers.length > 0 && (
                        <div className="mb-4">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <Users className="h-4 w-4" />
                            <span>Team Members</span>
                          </div>
                          <div className="space-y-2">
                            {teamMembers.map((member) => (
                              <div
                                key={member.id}
                                className="flex items-center p-3 bg-secondary/50 rounded-lg border border-border"
                              >
                                <div className="grid grid-cols-3 gap-4 flex-1 min-w-0">
                                  <div className="truncate">
                                    <span className="text-xs text-muted-foreground block">Name</span>
                                    <span className="text-sm font-medium">{member.name}</span>
                                  </div>
                                  <div className="truncate">
                                    <span className="text-xs text-muted-foreground block">Role</span>
                                    <span className="text-sm">{member.role}</span>
                                  </div>
                                  <div className="truncate">
                                    <span className="text-xs text-muted-foreground block">Email</span>
                                    <span className="text-sm">{member.email || "-"}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}

                      {/* Special display for D3 - Containment Actions */}
                      {step.step_number === 3 && (
                        <div className="mb-4">
                          <ActionManager 
                            claimId={claimId} 
                            stepNumber={3} 
                            title="Containment Actions"
                          />
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}

                      {/* Special display for D4 - Root Cause Analysis */}
                      {step.step_number === 4 && (
                        <div className="mb-4">
                          <RootCauseAnalysis claimId={claimId} />
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}

                      {/* Special display for D5 - Permanent Corrective Actions */}
                      {step.step_number === 5 && (
                        <div className="mb-4">
                          <ActionManager 
                            claimId={claimId} 
                            stepNumber={5} 
                            title="Permanent Corrective Actions"
                          />
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}

                      {/* Special display for D6 - Implement & Validate */}
                      {step.step_number === 6 && (
                        <div className="mb-4">
                          <ActionManager 
                            claimId={claimId} 
                            stepNumber={6} 
                            title="Implementation & Validation Tasks"
                          />
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}

                      {/* Special display for D7 - Prevent Recurrence */}
                      {step.step_number === 7 && (
                        <div className="mb-4">
                          <PreventRecurrence claimId={claimId} />
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}

                      {/* Special display for D8 - Congratulate Team */}
                      {step.step_number === 8 && (
                        <div className="mb-4">
                          <TeamRecognition claimId={claimId} />
                          {step.content && <div className="border-t border-border mt-4 pt-4" />}
                        </div>
                      )}
                      {editingStep === step.step_number ? (
                        <div className="space-y-4">
                          {step.step_number === 2 ? (
                            <RichTextEditor
                              value={stepContent}
                              onChange={setStepContent}
                              placeholder="Describe the problem in detail..."
                              bucket="8d-images"
                            />
                          ) : (
                            <Textarea
                              value={stepContent}
                              onChange={(e) => setStepContent(e.target.value)}
                              placeholder="Enter step content..."
                              rows={4}
                            />
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() =>
                                updateStepMutation.mutate({
                                  stepNumber: step.step_number,
                                  content: stepContent,
                                  status: "in_progress",
                                })
                              }
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                updateStepMutation.mutate({
                                  stepNumber: step.step_number,
                                  content: stepContent,
                                  status: "completed",
                                })
                              }
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Complete
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingStep(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Show rejection notice if step was rejected */}
                          {step.rejected_at && step.rejection_note && (
                            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                              <div className="flex items-center gap-2 text-destructive mb-2">
                                <AlertTriangle className="h-4 w-4" />
                                <span className="font-medium text-sm">Previously Rejected</span>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(step.rejected_at), "MMM d, yyyy HH:mm")}
                                </span>
                              </div>
                              <p className="text-sm text-destructive/80">{step.rejection_note}</p>
                            </div>
                          )}
                          
                          {step.content ? (
                            step.step_number === 2 ? (
                              <div 
                                className="prose prose-invert prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: step.content }}
                              />
                            ) : (
                              <p className="text-sm whitespace-pre-wrap">{step.content}</p>
                            )
                          ) : (
                            <p className="text-sm text-muted-foreground italic">
                              No content yet
                            </p>
                          )}
                          
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingStep(step.step_number);
                                setStepContent(step.content || "");
                              }}
                            >
                              Edit
                            </Button>
                            {step.status === "completed" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-destructive hover:text-destructive/80 border-destructive/30 hover:border-destructive/50 hover:bg-destructive/10"
                                onClick={() => setRejectingStep({ stepNumber: step.step_number, title: step.title })}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Comments */}
          <Card>
            <CardHeader>
              <CardTitle>Communication Log</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {comments?.map((comment) => (
                  <div
                    key={comment.id}
                    className={`p-4 rounded-lg ${
                      comment.author_type === "factory"
                        ? "bg-primary/10 border border-primary/20"
                        : "bg-secondary/50 border border-border"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                      <span className="font-medium">
                        {comment.author_type === "factory" ? "Factory" : "Supplier"}
                      </span>
                      <span>•</span>
                      <span>{format(new Date(comment.created_at), "MMM d, yyyy HH:mm")}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{comment.content}</p>
                  </div>
                ))}
                {!comments?.length && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No comments yet
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && newComment.trim()) {
                      e.preventDefault();
                      addCommentMutation.mutate();
                    }
                  }}
                />
                <Button
                  size="icon"
                  disabled={!newComment.trim() || addCommentMutation.isPending}
                  onClick={() => addCommentMutation.mutate()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Supplier Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                <div>
                  {claim.supplier?.name ? (
                    <RouterLink
                      to={`/supplier-quality/${claim.supplier_id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      {claim.supplier.name}
                    </RouterLink>
                  ) : (
                    <p className="font-medium">Unknown supplier</p>
                  )}
                  <p className="text-muted-foreground">{claim.supplier?.code}</p>
                </div>
              </div>
              {claim.supplier?.contact_email && (
                <p className="text-muted-foreground">{claim.supplier.contact_email}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Claim Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Created {format(new Date(claim.created_at), "MMM d, yyyy")}</span>
              </div>
              {claim.creator && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>{claim.creator.full_name || claim.creator.email}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Link className="h-4 w-4" />
                Supplier Portal Link
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Share this link with the supplier to give them access to this claim.
              </p>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={`https://app.factory-pilot.ai/8d/supplier/${claim.id}?token=${claim.magic_token}`}
                  className="text-xs font-mono"
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`https://app.factory-pilot.ai/8d/supplier/${claim.id}?token=${claim.magic_token}`);
                    toast({ title: "Link copied to clipboard" });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Expires: {expiresAt ? format(expiresAt, "MMM d, yyyy") : "-"}
                </span>
                {isExpired && <Badge variant="destructive">Expired</Badge>}
                {!isExpired && isNearExpiry && <Badge variant="outline">Almost expired</Badge>}
              </div>
              {(isExpired || isNearExpiry) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => regenerateTokenMutation.mutate()}
                  disabled={regenerateTokenMutation.isPending}
                >
                  {regenerateTokenMutation.isPending ? "Regenerating..." : "Regenerate link"}
                </Button>
              )}
            </CardContent>
          </Card>

          <FileAttachments claimId={claimId} authorType="factory" />
        </div>
      </div>

      {/* Rejection Dialog */}
      <Dialog open={!!rejectingStep} onOpenChange={(open) => !open && setRejectingStep(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Reject Step D{rejectingStep?.stepNumber}
            </DialogTitle>
            <DialogDescription>
              {rejectingStep?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Please provide feedback explaining why this step requires revision. The supplier will be notified via email.
            </p>
            <Textarea
              placeholder="Enter rejection reason and required changes..."
              value={rejectionNote}
              onChange={(e) => setRejectionNote(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectingStep(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectionNote.trim() || rejectStepMutation.isPending}
              onClick={() => {
                if (rejectingStep) {
                  rejectStepMutation.mutate({
                    stepNumber: rejectingStep.stepNumber,
                    rejectionNote,
                    stepTitle: rejectingStep.title,
                  });
                }
              }}
            >
              {rejectStepMutation.isPending ? "Rejecting..." : "Reject & Notify Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
